import React, {useRef} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  useToast,
  Avatar,
  Select,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialog,
} from '@chakra-ui/react';
import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import {useMutation, useQuery} from '@tanstack/react-query';
import * as yup from 'yup';
import {api} from '../../../api';
import {getErrorMessage} from '../../../utils/string';
import {roles, BlacklistScope} from '../../../config';
import {formatDate} from '../../../utils/date';
import useDisclosure from '../../../hooks/useDisclosure';
import {routes} from '../../../config/routes';
import {ReadOnlyInfo, Page} from '../../../components';
import {pick} from '../../../utils/object';
import UserChannels from '../../../components/channels/UserChannels';

const schema = yup
  .object({
    thumbnail: yup.string(),
    fullname: yup.string().required('Bu alan zorunludur.'),
    role: yup.string().oneOf(roles).required('Bu alan zorunludur.'),
    email: yup
      .string()
      .email('Geçersiz e-posta adresi')
      .required('Bu alan zorunludur'),
  })
  .required();

const formKeys = ['thumbnail', 'fullname', 'role', 'email'];

const EditUser = () => {
  const {id} = useParams();
  const toast = useToast();
  const deleteModal = useDisclosure();
  const banDevicesModal = useDisclosure();
  const unbanModal = useDisclosure();
  const cancelRef = useRef();
  const banDevicesCancelRef = useRef();
  const unbanCancelRef = useRef();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    trigger,
    formState: {errors},
    reset,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const {mutateAsync, isPending} = useMutation({
    mutationFn: values => api.manageUser(id, values),
  });

  const {mutateAsync: deleteUser, isPending: isDeleting} = useMutation({
    mutationFn: () => api.deleteUser(id),
  });

  const {
    data: userDevices,
    refetch: refetchUserDevices,
  } = useQuery({
    queryKey: ['user-device-ids', id],
    queryFn: () => api.getUserDeviceIds(id).then(res => res.data),
    enabled: !!id,
  });

  const {data: userBans, refetch: refetchUserBans} = useQuery({
    queryKey: ['user-bans', id],
    queryFn: () => api.getUserBans(id).then(res => res.data),
    enabled: !!id,
  });

  const activeBans = (userBans?.bans || []).filter(
    ban =>
      ban.isActive &&
      (!ban.expiresAt || new Date(ban.expiresAt) > new Date()),
  );

  const {mutateAsync: unbanUser, isPending: isUnbanning} = useMutation({
    mutationFn: () => api.unbanUser(id),
  });

  const onUnban = async () => {
    try {
      const {data: result} = await unbanUser();
      unbanModal.close();
      toast({
        title: 'Kullanıcının banı kaldırıldı.',
        description: `${result?.removedCount ?? 0} ban kaydı silindi. Değişiklik en geç 1 dakika içinde etkili olur.`,
        status: 'success',
        position: 'top',
      });
      refetchUserBans();
    } catch (error) {
      toast({
        title: getErrorMessage(error),
        status: 'error',
        position: 'top',
      });
    }
  };

  const {mutateAsync: banDevices, isPending: isBanningDevices} = useMutation({
    mutationFn: () =>
      api.banUserDevices({
        userId: id,
        scopes: [
          BlacklistScope.Access,
          BlacklistScope.Register,
          BlacklistScope.ChannelMessage,
        ],
      }),
  });

  const onBanDevices = async () => {
    try {
      const {data: result} = await banDevices();
      banDevicesModal.close();
      toast({
        title: 'Kullanıcının cihazları engellendi.',
        description: `Uygulamaya giriş, kayıt olma ve kanala mesaj gönderme cihaz bazında engellendi. ${result?.createdCount ?? 0} yeni kayıt oluşturuldu, ${result?.skippedCount ?? 0} kayıt zaten mevcuttu.`,
        status: 'success',
        position: 'top',
      });
      refetchUserDevices();
      refetchUserBans();
    } catch (error) {
      toast({
        title: getErrorMessage(error),
        status: 'error',
        position: 'top',
      });
    }
  };

  const {data} = useQuery({
    queryKey: ['user', id],
    queryFn: () =>
      api
        .getUser(id)
        .then(res => res.data)
        .then(values => {
          reset({...values, thumbnail: values.thumbnail || ''});
          return values;
        }),
  });

  const onSubmit = async values => {
    try {
      const {data} = await mutateAsync(pick(values, formKeys));
      if (data) {
        toast({
          title: 'Bilgiler kaydedildi.',
          status: 'success',
          position: 'top',
        });
      }
    } catch (error) {
      toast({
        title: getErrorMessage(error),
        status: 'error',
        position: 'top',
      });
    }
  };

  const onDelete = async () => {
    try {
      await deleteUser();
      toast({
        title: 'Kullanıcı silindi.',
        status: 'success',
        position: 'top',
      });
      navigate(routes.users.path);
    } catch (error) {
      toast({
        title: getErrorMessage(error),
        status: 'error',
        position: 'top',
      });
    }
  };

  return (
    <Page>
      <Box
        bg={'white'}
        overflow={'scroll'}
        borderRadius={'md'}
        display={'flex'}
        flexDirection={'column'}
        boxShadow={'md'}
        p={'4'}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Flex
            zIndex="2"
            direction="column"
            maxW="100%"
            background="transparent"
            borderRadius="md"
            me="auto">
            <Box display={'flex'} flexDirection="column">
              <Avatar
                name={data?.fullname}
                src={getValues('thumbnail')}
                size={'xl'}
                m={'4'}
                alignSelf={'center'}
              />
              <Input
                type={'hidden'}
                defaultValue={getValues('thumbnail')}
                {...register('thumbnail')}
              />
              <Button
                alignSelf={'center'}
                variant={'ghost'}
                onClick={() => {
                  setValue('thumbnail', '');
                  trigger('thumbnail');
                }}>
                Kaldır
              </Button>
            </Box>
            <ReadOnlyInfo label={'ID'} value={data?.id} />
            <FormControl isInvalid={!!errors.fullname} mb="4">
              <FormLabel
                display="flex"
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                mb="8px">
                Ad Soyad
              </FormLabel>
              <Input
                fontSize="sm"
                type="text"
                fontWeight="500"
                size="md"
                defaultValue={data?.fullname}
                {...register('fullname')}
              />
              <FormErrorMessage>{errors.fullname?.message}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={!!errors.role} mb="4">
              <FormLabel
                display="flex"
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                mb="8px">
                Rol
              </FormLabel>
              <Select
                fontSize="sm"
                placeholder="Rol seçin"
                fontWeight="500"
                defaultValue={data?.role}
                size="md"
                {...register('role')}>
                {roles.map(role => {
                  return (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  );
                })}
              </Select>
              <FormErrorMessage>{errors.role?.message}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={!!errors.email} mb="4">
              <FormLabel
                display="flex"
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                mb="8px">
                E-Posta
              </FormLabel>
              <Input
                fontSize="sm"
                type="email"
                fontWeight="500"
                size="md"
                defaultValue={data?.email}
                {...register('email')}
              />
              <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
            </FormControl>
            <ReadOnlyInfo
              label={'Kayıt Tarihi'}
              value={formatDate(data?.createdAt)}
            />
            <ReadOnlyInfo
              label={'Son Aktivite Tarihi'}
              value={formatDate(data?.lastActivityAt)}
            />
            <ReadOnlyInfo
              label={'Giriş Yöntemi'}
              value={
                data?.authProvider === 'google'
                  ? '🔵 Google'
                  : data?.authProvider === 'apple'
                    ? '🍎 Apple'
                    : '📧 E-posta'
              }
            />
            {data?.googleId && (
              <ReadOnlyInfo label={'Google ID'} value={data?.googleId} />
            )}
            {data?.appleId && (
              <ReadOnlyInfo label={'Apple ID'} value={data?.appleId} />
            )}
            <ReadOnlyInfo label={'Bildirim Tokenı'} value={data?.deviceToken} />
            <ReadOnlyInfo label={'Cihaz'} value={data?.platform} />
            <ReadOnlyInfo
              label={'Kayıtlı Cihaz ID’leri'}
              value={
                userDevices?.deviceIds?.length
                  ? userDevices.deviceIds.join(', ')
                  : '—'
              }
            />
            <ReadOnlyInfo
              label={'Ban Durumu'}
              value={
                activeBans.length
                  ? `🚫 Banlı (${activeBans.length} aktif kayıt: ${Array.from(
                      new Set(activeBans.map(ban => `${ban.type}/${ban.scope}`)),
                    ).join(', ')})`
                  : '✅ Banlı değil'
              }
            />
            <Button
              isLoading={isPending}
              colorScheme={'primary'}
              isDisabled={isPending}
              type="submit"
              fontSize={'sm'}>
              Kaydet
            </Button>
          </Flex>
        </form>
      </Box>
      <Box display={'flex'} justifyContent={'end'} gap={'2'}>
        <Button
          isLoading={isUnbanning}
          colorScheme={'green'}
          isDisabled={isUnbanning || !userBans?.bans?.length}
          type="button"
          my={'4'}
          onClick={unbanModal.open}
          fontSize={'sm'}>
          Banı Kaldır
        </Button>
        <Button
          isLoading={isBanningDevices}
          colorScheme={'orange'}
          isDisabled={isBanningDevices || !userDevices?.deviceIds?.length}
          type="button"
          my={'4'}
          onClick={banDevicesModal.open}
          fontSize={'sm'}>
          Tüm Cihazlarını Banla
        </Button>
        <Button
          isLoading={isDeleting}
          colorScheme={'red'}
          isDisabled={isDeleting}
          type="button"
          my={'4'}
          onClick={deleteModal.open}
          fontSize={'sm'}>
          Kullanıcıyı Sil
        </Button>
      </Box>
      <Box
        bg={'white'}
        overflow={'scroll'}
        borderRadius={'md'}
        display={'flex'}
        flexDirection={'column'}
        boxShadow={'md'}
        p={'4'}>
        <UserChannels userId={id} />
      </Box>
      <AlertDialog
        closeOnOverlayClick
        closeOnEsc
        leastDestructiveRef={cancelRef}
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.close}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Kullanıcyı Sil
            </AlertDialogHeader>
            <AlertDialogBody>
              Kullanıcıyı silmek istediğinize emin misiniz?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={deleteModal.close}>
                Vazgeç
              </Button>
              <Button
                colorScheme="red"
                onClick={onDelete}
                ml={3}
                isLoading={isDeleting}
                disabled={isDeleting}>
                Sil
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      <AlertDialog
        closeOnOverlayClick
        closeOnEsc
        leastDestructiveRef={banDevicesCancelRef}
        isOpen={banDevicesModal.isOpen}
        onClose={banDevicesModal.close}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Tüm Cihazlarını Banla
            </AlertDialogHeader>
            <AlertDialogBody>
              Kullanıcının {userDevices?.deviceIds?.length ?? 0} cihazı için
              "Uygulamaya Giriş", "Kayıt Olma" ve "Kanala Mesaj Gönderme"
              kapsamlarında cihaz bazlı engel uygulanacak. Bu cihazlar artık
              uygulamaya giriş yapamayacak, yeni hesap açamayacak ve mesaj
              gönderemeyecek. Devam edilsin mi?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={banDevicesCancelRef}
                onClick={banDevicesModal.close}>
                Vazgeç
              </Button>
              <Button
                colorScheme="orange"
                onClick={onBanDevices}
                ml={3}
                isLoading={isBanningDevices}
                disabled={isBanningDevices}>
                Banla
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      <AlertDialog
        closeOnOverlayClick
        closeOnEsc
        leastDestructiveRef={unbanCancelRef}
        isOpen={unbanModal.isOpen}
        onClose={unbanModal.close}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Banı Kaldır
            </AlertDialogHeader>
            <AlertDialogBody>
              Kullanıcının ID, e-posta ve cihazlarına ait{' '}
              {userBans?.bans?.length ?? 0} ban kaydı silinecek. Kullanıcı
              tekrar giriş yapabilecek, kayıt olabilecek ve mesaj
              gönderebilecek. IP banları bu işlemden etkilenmez. Devam edilsin
              mi?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={unbanCancelRef} onClick={unbanModal.close}>
                Vazgeç
              </Button>
              <Button
                colorScheme="green"
                onClick={onUnban}
                ml={3}
                isLoading={isUnbanning}
                disabled={isUnbanning}>
                Banı Kaldır
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Page>
  );
};

export default EditUser;
